import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStudio } from '@/store/StudioStore';
import { CustomEntryField, FieldLabel, MultiChipSelect, Section as RawSection, SimpleSelect } from '@/components/StudioBits';
import FontPicker from '@/components/FontPicker';
import FontLibrary from '@/components/FontLibrary';
import CreativeFontLibrary from '@/components/CreativeFontLibrary';
import { CREATIVE_FONTS, CREATIVE_FONT_CATEGORIES, searchCreativeFonts } from '@/data/creativeFonts';
import { FONT_DATABASE } from '@/data/fontDatabase';
import ColorLab from '@/components/ColorLab';
import { CREATION_TYPES, TRANSPARENT_RECOMMENDED, TOPIC_CATEGORIES, TOPIC_ITEMS, FONT_FAMILIES, FONT_STYLE_MODIFIERS, LETTER_CONSTRUCTIONS } from '@/data/optionsCore';
import { LAYOUTS, PATTERN_FAMILIES, MATERIAL_FAMILIES, FINISHES, LAYER_POSITIONS, INTERNAL_DETAILS, SUPPORTING_ELEMENTS, LIGHTING, CAMERA_ANGLES, BACKGROUND_MODES, COMPOSITION_LAYOUTS, HIERARCHY_PRESETS, PALETTES } from '@/data/optionsLibrary';
import { GradientEditor } from '@/components/ColorLab';
import { LETTERING_CONTROLS, SPACING_PRESETS, CHARACTER_SET_PRESETS } from '@/data/letteringControls';
import { HOLIDAY_TREATMENTS } from '@/data/holidayTreatments';
import { compileMasterPrompt, buildRecipe, detectConflicts } from '@/lib/promptCompiler';

// Numbered 11-section navigation. Each section maps to existing tab content so nothing is removed.
const SECTIONS = [
  { id: 's1',  n: 1,  label: 'Exact Text',                                tab: 'quick',       hint: 'Exact wording, spelling lock, capitalization, hierarchy.' },
  { id: 's2',  n: 2,  label: 'Purpose & Product',                          tab: 'quick',       hint: 'What are you creating? T-shirt, tumbler, flyer, logo, custom.' },
  { id: 's3',  n: 3,  label: 'Theme & Creative Direction',                 tab: 'holiday',     hint: 'Topic, holiday, occasion, industry, audience, mood.' },
  { id: 's4',  n: 4,  label: 'Font Library',                               tab: 'quick',       hint: 'Named fonts, family, direction, pairing.' },
  { id: 's5',  n: 5,  label: 'Text Construction & Layout',                 tab: 'advanced',    hint: 'Construction, stack, arch, curve, path, shape, distortion.' },
  { id: 's6',  n: 6,  label: 'Materials, Fills, Patterns & Finishes',      tab: 'advanced',    hint: 'Chrome, chenille, glass, liquid, animal, floral, futuristic.' },
  { id: 's7',  n: 7,  label: 'Color Lab & Gradients',                      tab: 'letter',      hint: 'Interactive picker, 85 palettes, harmonies, gradient stops.' },
  { id: 's8',  n: 8,  label: 'Effects & Layer Stack',                      tab: 'advanced',    hint: 'Inner keyline, outlines, bevel, extrusion, shadow, glow.' },
  { id: 's9',  n: 9,  label: 'Per-Word & Per-Letter Mixer',                tab: 'letter',      hint: 'Batch selectors, individual letter overrides, coordinated mix.' },
  { id: 's10', n: 10, label: 'Supporting Elements & Composition',          tab: 'supporting',  hint: 'Props, decorations, atmosphere, composition, hierarchy.' },
  { id: 's11', n: 11, label: 'Background, Export & Review',                tab: 'quick',       hint: 'Transparent PNG, mockup, canvas size, review your selections.' },
];

const TABS = [
  { id: 'quick', label: 'Quick Create' },
  { id: 'advanced', label: 'Advanced Typography' },
  { id: 'letter', label: 'Per-Letter Mixer' },
  { id: 'holiday', label: 'Holidays + Topics' },
  { id: 'supporting', label: 'Supporting Elements' },
  { id: 'composition', label: 'Composition' },
];

export default function Studio() {
  const { state, dispatch } = useStudio();
  const [reviewOpen, setReviewOpen] = useState(false);
  const nav = useNavigate();

  const set = (key, value) => dispatch({ type: 'SET', key, value });
  const setCustom = (key, value) => dispatch({ type: 'CUSTOM', key, value });
  const toggle = (key, value) => dispatch({ type: 'TOGGLE_ARR', key, value });
  const recipe = useMemo(() => buildRecipe(state), [state]);
  const conflicts = useMemo(() => detectConflicts(state), [state]);

  const surprise = () => {
    const palettes = Object.keys(require('@/data/optionsLibrary').PALETTES);
    const materials = Object.keys(require('@/data/optionsLibrary').MATERIAL_FAMILIES);
    const finishes = require('@/data/optionsLibrary').FINISHES;
    const layouts = Object.values(require('@/data/optionsLibrary').LAYOUTS)[2];
    const pick = (a) => a[Math.floor(Math.random() * a.length)];
    dispatch({ type: 'PATCH', patch: {
      fontFamily: 'display', fontFamilyLabel: 'Display', fontStyle: pick(['Chunky','Bubble','Y2K','Varsity / Collegiate']),
      palette: pick(palettes), materialFamily: pick(materials), finish: pick(finishes), layoutArch: pick(layouts),
    } });
    toast.success('Surprise mix loaded');
  };
  const randomFonts = () => {
    const styles = ['Athletic / Block','Chunky','Bubble','Blackletter','Dripping','Formal Calligraphy','Y2K','Varsity / Collegiate'];
    dispatch({ type: 'PATCH', patch: { fontStyle: styles[Math.floor(Math.random() * styles.length)] } });
  };
  const randomColors = () => {
    const palettes = Object.keys(require('@/data/optionsLibrary').PALETTES);
    dispatch({ type: 'SET', key: 'palette', value: palettes[Math.floor(Math.random() * palettes.length)] });
  };
  const randomEffects = () => {
    const finishes = require('@/data/optionsLibrary').FINISHES;
    dispatch({ type: 'SET', key: 'finish', value: finishes[Math.floor(Math.random() * finishes.length)] });
  };
  const duplicate = () => {
    const dup = { ...state, id: undefined, name: (state.exactText || 'Untitled') + ' (copy)' };
    dispatch({ type: 'LOAD', project: dup });
    toast.success('Recipe duplicated');
  };

  const chooseCreation = (id) => {
    const c = CREATION_TYPES.find((x) => x.id === id);
    dispatch({ type: 'PATCH', patch: { creationType: id, creationTypeLabel: c?.label || id } });
    if (TRANSPARENT_RECOMMENDED.includes(id) && !state.background) {
      const bg = BACKGROUND_MODES[0];
      dispatch({ type: 'PATCH', patch: { background: bg.id, backgroundLabel: bg.label } });
      toast('Transparent PNG recommended for this creation type.');
    }
  };

  const chooseTopicCategory = (id) => {
    const c = TOPIC_CATEGORIES.find((x) => x.id === id);
    dispatch({ type: 'PATCH', patch: { topicCategory: id, topicCategoryLabel: c?.label || id, topicItem: '' } });
  };

  const chooseFontFamily = (id) => {
    const f = FONT_FAMILIES.find((x) => x.id === id);
    dispatch({ type: 'PATCH', patch: { fontFamily: id, fontFamilyLabel: f?.label || id, fontStyle: '' } });
  };

  const chooseBackground = (id) => {
    const b = BACKGROUND_MODES.find((x) => x.id === id);
    dispatch({ type: 'PATCH', patch: { background: id, backgroundLabel: b?.label || id } });
  };

  const generate = () => {
    if (!state.exactText || !state.exactText.trim()) { toast.error('Enter your exact text first.'); return; }
    nav('/result');
  };

  const reset = () => { if (confirm('Reset all selections?')) dispatch({ type: 'RESET' }); };

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-8 relative z-10 overflow-x-hidden" data-testid="studio-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow">Unified Studio</div>
          <h1 className="font-display text-4xl sm:text-5xl">Build Your Prompt</h1>
        </div>
        <div className="hidden lg:flex gap-2">
          <button data-testid="reset-btn" onClick={reset} className="chrome-border chrome-hover rounded-full px-5 py-2 text-xs uppercase tracking-widest">Reset</button>
          <button data-testid="generate-prompt-btn" onClick={generate} className="btn-magenta rounded-full px-6 py-2 text-xs uppercase tracking-widest">Generate Prompt</button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button data-testid="tb-undo" onClick={() => dispatch({ type: 'UNDO' })} className="chip">↶ Undo</button>
        <button data-testid="tb-redo" onClick={() => dispatch({ type: 'REDO' })} className="chip">↷ Redo</button>
        <button data-testid="tb-duplicate" onClick={duplicate} className="chip">Duplicate Recipe</button>
        <span className="w-px bg-white/10 mx-1" />
        <button data-testid="tb-surprise" onClick={surprise} className="chip">✨ Surprise Me</button>
        <button data-testid="tb-random-fonts" onClick={randomFonts} className="chip">Random Font</button>
        <button data-testid="tb-random-colors" onClick={randomColors} className="chip">Random Colors</button>
        <button data-testid="tb-random-effects" onClick={randomEffects} className="chip">Random Effect</button>
      </div>

      {conflicts.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="conflict-banner">
          <div className="eyebrow text-amber-400">Heads up · compatibility</div>
          {conflicts.map((w, i) => <p key={i} className="text-xs text-amber-100">⚠ {w}</p>)}
          <p className="text-[10px] text-amber-200">You can Keep Combination or adjust manually. Nothing was changed for you.</p>
        </div>
      )}

      <div className="mb-4 rounded-xl chrome-border bg-zinc-900/60 px-4 py-3 flex items-start gap-3">
        <span className="text-pink-500 text-lg leading-none">◉</span>
        <p className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-white">Every dropdown has a matching &ldquo;Or type your own&rdquo; box.</strong> Nothing here is a limit. Type any subject, font direction, material, pattern, effect, prop, or vibe you can imagine — it goes straight into your final prompt.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        {/* Center content — all controls, one continuous scroll */}
        <div className="space-y-4">
          <QuickTab state={state} set={set} setCustom={setCustom} dispatch={dispatch} chooseCreation={chooseCreation} chooseTopicCategory={chooseTopicCategory} chooseFontFamily={chooseFontFamily} chooseBackground={chooseBackground} />
          <HolidayTab state={state} set={set} setCustom={setCustom} chooseTopicCategory={chooseTopicCategory} />
          <AdvancedTab state={state} set={set} setCustom={setCustom} toggle={toggle} />
          <SupportingTab state={state} toggle={toggle} setCustom={setCustom} />
          <CompositionTab state={state} set={set} setCustom={setCustom} />
          <LetterMixerTab state={state} dispatch={dispatch} />
        </div>

        {/* Right recipe sidebar */}
        <aside className="space-y-4">
          <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5 xl:sticky xl:top-20">
            <div className="eyebrow mb-2">Your Selections</div>
            <h4 className="font-display text-xl mb-3">{state.exactText ? `\u201C${state.exactText}\u201D` : 'Your recipe'}</h4>
            <div className="space-y-2 max-h-[420px] overflow-y-auto scrollbar-thin pr-2" data-testid="recipe-list">
              {recipe.length === 0 && <p className="text-sm text-zinc-500">Start making selections and your recipe fills in here.</p>}
              {recipe.map((e, i) => (
                <div key={i} className="flex justify-between gap-3 text-xs border-b border-white/5 pb-1">
                  <span className="text-zinc-500 uppercase tracking-wider">{e.k}</span>
                  <span className="text-zinc-100 text-right">{e.v}</span>
                </div>
              ))}
            </div>
            <button data-testid="review-btn" onClick={() => setReviewOpen(true)} className="chrome-border rounded-full w-full mt-3 py-2 text-xs uppercase tracking-widest">Review All</button>
            <button data-testid="sidebar-generate" onClick={generate} className="btn-magenta w-full mt-2 rounded-full py-2 text-xs uppercase tracking-widest">Generate Prompt</button>
          </div>
        </aside>
      </div>

      {/* Review modal */}
      {reviewOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur p-4 overflow-y-auto" onClick={() => setReviewOpen(false)} data-testid="review-modal">
          <div className="max-w-3xl mx-auto rounded-2xl chrome-border bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div><div className="eyebrow">Review · Every Selection</div><h3 className="font-display text-3xl">Ready To Generate?</h3></div>
              <button onClick={() => setReviewOpen(false)} className="chrome-border rounded-full px-4 py-1.5 text-xs uppercase tracking-widest">Close</button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
              {recipe.length === 0 && <p className="text-sm text-zinc-500">No selections yet. Scroll up to Exact Text and start building.</p>}
              {recipe.map((e, i) => (
                <div key={i} className="flex justify-between gap-3 text-sm border-b border-white/5 py-2">
                  <span className="text-zinc-400 uppercase tracking-wider text-xs">{e.k}</span>
                  <span className="text-white text-right">{e.v}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-6">
              <button data-testid="review-close" onClick={() => setReviewOpen(false)} className="chrome-border rounded-full px-5 py-2 text-xs uppercase tracking-widest">Keep Editing</button>
              <button data-testid="review-generate" onClick={() => { setReviewOpen(false); generate(); }} className="btn-magenta rounded-full px-6 py-2 text-xs uppercase tracking-widest ml-auto">Generate Prompt</button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky mobile bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-white/10 px-3 pt-3 safe-bottom flex gap-2" role="toolbar" aria-label="Studio actions">
        <button data-testid="mobile-reset" onClick={reset} className="flex-1 chrome-border rounded-full py-3 text-xs uppercase tracking-widest" aria-label="Reset the studio">Reset</button>
        <button data-testid="mobile-generate" onClick={generate} className="flex-1 btn-magenta rounded-full py-3 text-xs uppercase tracking-widest" aria-label="Generate prompt">Generate</button>
      </div>
    </main>
  );
}

/* ---------- Tabs ---------- */

// Section group → 11-step slug mapping. Only render sections belonging to the active step.
const GROUP = {
  'Exact Text': 's1',
  'What Are You Creating?': 's2',
  'Topic / World': 's3',
  'Font Family & Direction': 's4',
  'Font Library · 251 Named Fonts': 's4',
  'Palette': 's7',
  'Background / Export': 's11',
  'Font Style Modifiers': 's5',
  'Letter Construction': 's5',
  'Layout & Distortion': 's5',
  'Pattern Fill': 's6',
  'Material': 's6',
  'Finish': 's6',
  'Layer Stack (Outlines / Bevel / Glow)': 's8',
  'Internal Letter Details': 's8',
  'Advanced Letter Selection': 's9',
  'Per-Letter / Per-Word Overrides': 's9',
  'Color Lab · HSL Wheel + Harmony': 's7',
};

const SectionCtx = React.createContext(null);
function S({ title, children, right }) {
  const only = React.useContext(SectionCtx);
  const grp = GROUP[title];
  if (only && grp && grp !== only) return null;
  return <RawSection title={title} right={right}>{children}</RawSection>;
}

function QuickTab({ state, set, setCustom, dispatch, chooseCreation, chooseTopicCategory, chooseFontFamily, chooseBackground, onlyGroup }) {
  const topicItems = TOPIC_ITEMS[state.topicCategory] || [];
  return (
    <SectionCtx.Provider value={onlyGroup}>
      <S title="Exact Text">
        <FieldLabel>What should the design say?</FieldLabel>
        <textarea data-testid="exact-text-input" rows={3} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-lg text-white focus:border-pink-500 outline-none transition-colors" placeholder='e.g. "SPOOKY SZN"' value={state.exactText} onChange={(e) => set('exactText', e.target.value)} />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <FieldLabel>Capitalization</FieldLabel>
            <SimpleSelect testId="capitalization-select" value={state.capitalization} onChange={(v) => set('capitalization', v)} options={['UPPERCASE','lowercase','Title Case','As Typed']} />
          </div>
          <div>
            <FieldLabel>Apply Styling To</FieldLabel>
            <SimpleSelect testId="apply-scope-select" value={state.applyScope} onChange={(v) => set('applyScope', v)} options={['complete design','per line','per word','alternating letters','individual letters']} />
          </div>
        </div>
      </S>

      <S title="What Are You Creating?">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {CREATION_TYPES.map((c) => (
            <button key={c.id} data-testid={`ctype-${c.id}`} onClick={() => chooseCreation(c.id)}
              className={`text-left rounded-xl px-3 py-3 text-xs transition-colors ${state.creationType === c.id ? 'bg-pink-500/20 border border-pink-500 text-white' : 'chrome-border chrome-hover bg-zinc-950 text-zinc-300'}`}>
              {c.label}
            </button>
          ))}
        </div>
        <CustomEntryField testId="creation-custom" value={state.customEntries.creation} onChange={(v) => setCustom('creation', v)} />
      </S>

      <S title="Topic / World">
        <div className="rounded-lg bg-pink-500/10 border border-pink-500/30 px-3 py-2 mb-3 text-xs text-pink-100">
          <strong>You are never limited to this list.</strong> Pick a category + subtopic, OR just type any topic below (Ghostface, boutique launch, Bluey party, Ford Bronco, whatever). Your typed value flows straight into the final prompt.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Category</FieldLabel>
            <SimpleSelect testId="topic-category-select" value={state.topicCategory} onChange={chooseTopicCategory} options={TOPIC_CATEGORIES} />
          </div>
          <div>
            <FieldLabel>Specific Topic</FieldLabel>
            <SimpleSelect testId="topic-item-select" value={state.topicItem} onChange={(v) => set('topicItem', v)} options={topicItems} />
          </div>
        </div>
        <CustomEntryField testId="topic-custom" label="Free-form topic (any subject, character, business, vibe)" placeholder="e.g. Ghostface, Bluey birthday, Ford Bronco launch, Saints football watch party…" value={state.topicCustom} onChange={(v) => set('topicCustom', v)} />
      </S>

      <S title="Font Family & Direction">
        <p className="text-xs text-zinc-400 mb-3">Tap a tile to preview your text in that direction. Switch tabs to browse Sans, Serif, Script, Display, Gothic, Handwritten & Horror.</p>
        <FontPicker
          sampleText={state.exactText}
          family={state.fontFamily}
          style={state.fontStyle}
          onPick={(famId, styleName) => {
            const f = FONT_FAMILIES.find((x) => x.id === famId);
            dispatch({ type: 'PATCH', patch: { fontFamily: famId, fontFamilyLabel: f?.label || famId, fontStyle: styleName } });
          }}
        />
        <CustomEntryField testId="font-custom" label="Or describe your own font direction" placeholder="e.g. dripping chrome slasher serif with knife-slice terminals" value={state.customEntries.font} onChange={(v) => setCustom('font', v)} />
      </S>

      <S title={`Font Library · ${FONT_DATABASE.length} Named Fonts`}>
        <FontLibrary sampleText={state.exactText} chosen={state.namedFont} onPick={(f) => dispatch({ type: 'PATCH', patch: { namedFont: f.name, namedFontClassification: f.classification } })} />
        <CustomEntryField testId="named-font-custom" label="Can't find your font? Type the exact font name" placeholder="e.g. Neue Haas Grotesk, Helvetica Now, Recoleta Alt…" value={state.customEntries.namedFont} onChange={(v) => setCustom('namedFont', v)} />
        <div className="text-[10px] text-zinc-500 mt-2" data-testid="font-count-summary">
          Preset Fonts: <span className="text-zinc-300">{FONT_DATABASE.length}</span> · Creative Reference Presets: <span className="text-zinc-300">{CREATIVE_FONTS.length}</span> · Custom Font Entry: <span className="text-zinc-300">Unlimited</span>
        </div>
      </S>

      <S title="Creative & Character-Inspired Lettering">
        <CreativeFontLibrary
          selectedId={state.creativeFont?.id || ''}
          onSelect={(f) => dispatch({ type: 'SET', key: 'creativeFont', value: f })}
        />
      </S>

      <S title="Or Type Any Font Or Lettering Style">
        <CustomFontDetailsBlock state={state} dispatch={dispatch} setCustom={setCustom} />
      </S>

      <S title="Build A Font Combo (Optional)">
        <FontComboBuilder state={state} dispatch={dispatch} />
      </S>

      <S title="Palette">
        <SimpleSelect testId="palette-select" value={state.palette} onChange={(v) => set('palette', v)} options={Object.keys(PALETTES)} />
        <CustomEntryField testId="palette-custom" placeholder="Describe your own palette (hex codes ok)" value={state.customEntries.palette} onChange={(v) => setCustom('palette', v)} />
      </S>

      <S title="Background / Export">
        <SimpleSelect testId="background-select" value={state.background} onChange={chooseBackground} options={BACKGROUND_MODES} />
        {state.background === 'product-mockup' && (
          <input data-testid="mockup-product-input" className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" placeholder="Which product? e.g. black t-shirt" value={state.mockupProduct} onChange={(e) => set('mockupProduct', e.target.value)} />
        )}
        <CustomEntryField testId="background-custom" value={state.customBackground} onChange={(v) => set('customBackground', v)} />
      </S>
    </SectionCtx.Provider>
  );
}

function AdvancedTab({ state, set, setCustom, toggle, onlyGroup }) {
  return (
    <SectionCtx.Provider value={onlyGroup}>
      <S title="Font Style Modifiers"><MultiChipSelect testIdPrefix="mod" options={FONT_STYLE_MODIFIERS} value={state.fontStyleModifiers} onToggle={(v) => toggle('fontStyleModifiers', v)} /><CustomEntryField testId="mod-custom" value={state.customEntries.mods} onChange={(v) => setCustom('mods', v)} /></S>

      <S title="Letter Construction"><SimpleSelect testId="construction-select" value={state.letterConstruction} onChange={(v) => set('letterConstruction', v)} options={LETTER_CONSTRUCTIONS} /><CustomEntryField testId="construction-custom" value={state.customEntries.construction} onChange={(v) => setCustom('construction', v)} /></S>

      <S title="Layout & Distortion">
        {Object.entries(LAYOUTS).map(([group, items]) => {
          const key = { 'Basic Arrangement': 'layoutBasic','Stacked Text': 'layoutStack','Arch & Curve': 'layoutArch','Text on Path': 'layoutPath','Shaped Text': 'layoutShape','Perspective': 'layoutPerspective','Distortion / Warp': 'layoutDistortion' }[group];
          return (
            <div key={group} className="space-y-2">
              <FieldLabel>{group}</FieldLabel>
              <SimpleSelect testId={`layout-${key}`} value={state[key]} onChange={(v) => set(key, v)} options={items} />
            </div>
          );
        })}
        <CustomEntryField testId="layout-custom" value={state.customEntries.layout} onChange={(v) => setCustom('layout', v)} />
      </S>

      <SpacingSection state={state} set={set} />
      <CustomLetteringSection state={state} set={set} setCustom={setCustom} />

      <S title="Pattern Fill">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><FieldLabel>Family</FieldLabel><SimpleSelect testId="pattern-family-select" value={state.patternFamily} onChange={(v) => { set('patternFamily', v); set('patternSubtype', ''); }} options={Object.keys(PATTERN_FAMILIES)} /></div>
          <div><FieldLabel>Subtype</FieldLabel><SimpleSelect testId="pattern-subtype-select" value={state.patternSubtype} onChange={(v) => set('patternSubtype', v)} options={PATTERN_FAMILIES[state.patternFamily] || []} /></div>
        </div>
        <FieldLabel>Placement</FieldLabel>
        <MultiChipSelect testIdPrefix="pat-place" options={['Face','Inset Center','Outline','Bevel','Behind Text','Supporting Elements','Background']} value={state.patternPlacement || []} onToggle={(v) => toggle('patternPlacement', v)} />
        <CustomEntryField testId="pattern-custom" value={state.customEntries.pattern} onChange={(v) => setCustom('pattern', v)} />
      </S>

      <S title="Material">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><FieldLabel>Family</FieldLabel><SimpleSelect testId="material-family-select" value={state.materialFamily} onChange={(v) => { set('materialFamily', v); set('materialSubtype', ''); }} options={Object.keys(MATERIAL_FAMILIES)} /></div>
          <div><FieldLabel>Subtype</FieldLabel><SimpleSelect testId="material-subtype-select" value={state.materialSubtype} onChange={(v) => set('materialSubtype', v)} options={MATERIAL_FAMILIES[state.materialFamily] || []} /></div>
        </div>
        <FieldLabel>Placement</FieldLabel>
        <MultiChipSelect testIdPrefix="mat-place" options={['Face','Inset Center','Outline','Bevel','Extrusion','Behind Text','Supporting Elements']} value={state.materialPlacement || []} onToggle={(v) => toggle('materialPlacement', v)} />
        <CustomEntryField testId="material-custom" value={state.customEntries.material} onChange={(v) => setCustom('material', v)} />
      </S>

      <S title="Finish"><SimpleSelect testId="finish-select" value={state.finish} onChange={(v) => set('finish', v)} options={FINISHES} /><CustomEntryField testId="finish-custom" value={state.customEntries.finish} onChange={(v) => setCustom('finish', v)} /></S>

      <S title="Layer Stack (Outlines / Bevel / Glow)">
        <MultiChipSelect testIdPrefix="layer" options={LAYER_POSITIONS} value={(state.layers || []).map((l) => l.position)} onToggle={(v) => {
          const cur = state.layers || [];
          const exists = cur.find((l) => l.position === v);
          set('layers', exists ? cur.filter((l) => l.position !== v) : [...cur, { position: v, color: '#FF107A', intensity: 60 }]);
        }} />
        {/* Independent per-outline Fill Type (Solid / Gradient). Backward-compatible:
            existing layers without fillType default to "Solid" and emit the same
            prompt wording. The gradient controls reuse the Color Lab GradientEditor. */}
        {(state.layers || []).filter((l) => /^Outline\s*\d*$/i.test(l.position)).map((layer) => {
          const idx = (state.layers || []).findIndex((l) => l.position === layer.position);
          const updateLayer = (patch) => {
            const next = (state.layers || []).map((l, i) => i === idx ? { ...l, ...patch } : l);
            set('layers', next);
          };
          const fillType = layer.fillType || 'Solid';
          const grad = layer.gradient || { type: 'Linear', angle: 90, stops: [{ color: '#FFFFFF', pos: 0, opacity: 100 }, { color: layer.color || '#FF107A', pos: 100, opacity: 100 }] };
          return (
            <div key={layer.position} className="mt-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-3 space-y-2" data-testid={`outline-settings-${layer.position.replace(/\s+/g,'').toLowerCase()}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="eyebrow">{layer.position} · Outline Fill Type</span>
                <div className="inline-flex rounded-full border border-zinc-800 overflow-hidden">
                  {['Solid','Gradient'].map((t) => (
                    <button
                      key={t}
                      data-testid={`outline-filltype-${layer.position.replace(/\s+/g,'').toLowerCase()}-${t.toLowerCase()}`}
                      onClick={() => updateLayer({ fillType: t, ...(t === 'Gradient' ? { gradient: grad } : {}) })}
                      className={`px-3 py-1 text-xs uppercase tracking-widest ${fillType === t ? 'bg-pink-500 text-white' : 'text-zinc-400 hover:text-white'}`}
                    >{t}</button>
                  ))}
                </div>
              </div>
              {fillType === 'Solid' ? (
                <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">HEX
                  <input
                    data-testid={`outline-hex-${layer.position.replace(/\s+/g,'').toLowerCase()}`}
                    value={layer.color || ''}
                    onChange={(e) => updateLayer({ color: e.target.value })}
                    placeholder="#FFFFFF"
                    className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono"
                  />
                </label>
              ) : (
                <div data-testid={`outline-gradient-${layer.position.replace(/\s+/g,'').toLowerCase()}`}>
                  <GradientEditor
                    gradient={grad}
                    onChange={(g) => updateLayer({ gradient: g })}
                    current={layer.color || '#FF107A'}
                  />
                </div>
              )}
            </div>
          );
        })}
        <CustomEntryField testId="layer-custom" value={state.customEntries.layer} onChange={(v) => setCustom('layer', v)} />
      </S>

      <S title="Internal Letter Details"><MultiChipSelect testIdPrefix="detail" options={INTERNAL_DETAILS} value={state.internalDetails} onToggle={(v) => toggle('internalDetails', v)} /><CustomEntryField testId="detail-custom" value={state.customEntries.detail} onChange={(v) => setCustom('detail', v)} /></S>
    </SectionCtx.Provider>
  );
}

function computeOccurrences(text) {
  const occs = [];
  const lines = (text || '').split('\n');
  const counts = {}; // char -> running count
  let globalIndex = 0;
  lines.forEach((line, lineIdx) => {
    // Split into words while tracking column
    let col = 0;
    const words = line.split(' ');
    words.forEach((word, wordIdx) => {
      for (let pos = 0; pos < word.length; pos++) {
        const ch = word[pos];
        const key = ch.toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
        const ordinal = counts[key];
        const suffix = ordinal === 1 ? 'st' : ordinal === 2 ? 'nd' : ordinal === 3 ? 'rd' : 'th';
        occs.push({
          id: `c-${globalIndex}`,
          globalIndex,
          char: ch,
          line: lineIdx,
          col,
          wordIndex: wordIdx,
          wordText: word,
          posInWord: pos,
          occurrenceOfChar: ordinal,
          label: `${ordinal}${suffix} "${ch}" in ${word ? `"${word}"` : `line ${lineIdx + 1}`}, character ${col + 1} on line ${lineIdx + 1}`,
        });
        globalIndex++;
        col++;
      }
      if (wordIdx < words.length - 1) { // space
        occs.push({ id: `c-${globalIndex}`, globalIndex, char: ' ', line: lineIdx, col, wordIndex: -1, wordText: '', posInWord: -1, occurrenceOfChar: 0, label: `space on line ${lineIdx + 1}` });
        globalIndex++;
        col++;
      }
    });
    if (lineIdx < lines.length - 1) {
      // newline pseudo — do not create tile
      globalIndex++;
    }
  });
  return occs;
}

function LetterMixerTab({ state, dispatch, onlyGroup }) {
  const occurrences = useMemo(() => computeOccurrences(state.exactText || ''), [state.exactText]);
  const [selectedOccId, setSelectedOccId] = useState('');
  const [target, setTarget] = useState('');
  const [font, setFont] = useState('');
  const [color, setColor] = useState('#FF107A');
  const [colorTouched, setColorTouched] = useState(false);
  const [material, setMaterial] = useState('');
  const [scopePrompt, setScopePrompt] = useState(null); // { matches, description }
  const VOWELS = 'AEIOUaeiou';
  const overrides = state.perLetterOverrides || [];
  const selectedOcc = occurrences.find(o => o.id === selectedOccId);

  // Find override for a given occurrence id (new-shape) — fallback: old-shape items match nothing here.
  const overrideForOcc = (occId) => overrides.find(o => o.occurrenceId === occId);

  // Apply preset — creates one override per matching occurrence (opt-in explicit action).
  const applyPresetToOccs = (label, filterFn) => {
    const matches = occurrences.filter(filterFn);
    if (!matches.length) return;
    const additions = matches
      .filter(occ => !overrideForOcc(occ.id))
      .map(occ => ({ id: `ov-${Date.now()}-${occ.id}`, occurrenceId: occ.id, char: occ.char, label: occ.label, overrides: { note: label } }));
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: [...overrides, ...additions] });
  };

  const selectTile = (occ) => {
    // Toggle deselect if already selected AND has override → remove that override
    if (selectedOccId === occ.id && overrideForOcc(occ.id)) {
      dispatch({ type: 'SET', key: 'perLetterOverrides', value: overrides.filter(o => o.occurrenceId !== occ.id) });
      setSelectedOccId(''); setTarget('');
      toast.success(`Deselected ${occ.label}`);
      return;
    }
    setSelectedOccId(occ.id);
    setTarget(occ.label);
    const existing = overrideForOcc(occ.id);
    if (existing) {
      setFont(existing.overrides.font || '');
      setColor(existing.overrides.color || '#FF107A');
      setColorTouched(!!existing.overrides.color);
      setMaterial(existing.overrides.material || '');
    } else {
      setFont(''); setMaterial(''); setColor('#FF107A'); setColorTouched(false);
    }
    setScopePrompt(null);
  };

  const buildPatch = () => {
    const patch = {};
    if (font.trim()) patch.font = font.trim();
    if (material.trim()) patch.material = material.trim();
    if (colorTouched && color) patch.color = color;
    return patch;
  };

  const saveOverrideToOccs = (list, patch) => {
    const next = overrides.filter(o => !list.some(m => m.id === o.occurrenceId));
    list.forEach(m => next.push({ id: `ov-${Date.now()}-${m.id}`, occurrenceId: m.id, char: m.char, label: m.label, overrides: { ...patch } }));
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: next });
    setFont(''); setMaterial(''); setColor('#FF107A'); setColorTouched(false); setTarget(''); setSelectedOccId(''); setScopePrompt(null);
    toast.success(`Saved override for ${list.length} occurrence${list.length === 1 ? '' : 's'}`);
  };

  // Resolve a typed target string to occurrences. Returns { matches, description }.
  const resolveTypedTarget = (raw) => {
    const t = (raw || '').trim();
    if (!t) return null;
    // Full line match (contains newline literal or exact line text)
    const lines = (state.exactText || '').split('\n');
    const lineIdx = lines.findIndex(l => l === t);
    if (lineIdx >= 0 && t.length > 0) {
      const bag = occurrences.filter(o => o.line === lineIdx && o.char !== ' ');
      if (bag.length) return { matches: bag, description: `all ${bag.length} characters on line ${lineIdx + 1} ("${t}")` };
    }
    // Whole-word match
    const wordMatches = occurrences.filter(o => o.wordText && o.wordText === t);
    if (wordMatches.length && t.length > 1) {
      return { matches: wordMatches, description: `all ${wordMatches.length} characters of "${t}"` };
    }
    // Substring within word(s)
    if (t.length > 1) {
      const bag = [];
      occurrences.forEach(o => {
        if (o.wordText && o.wordText.includes(t)) {
          const start = o.wordText.indexOf(t);
          if (o.posInWord >= start && o.posInWord < start + t.length) bag.push(o);
        }
      });
      if (bag.length) return { matches: bag, description: `"${t}" wherever it appears (${bag.length} chars)` };
      return null;
    }
    // Single char: every occurrence
    const charMatches = occurrences.filter(o => o.char.toLowerCase() === t.toLowerCase());
    if (charMatches.length) return { matches: charMatches, description: `every "${t}" (${charMatches.length} occurrences)` };
    return null;
  };

  const addOverride = () => {
    const patch = buildPatch();
    if (!Object.keys(patch).length) return toast.error('Fill at least one field (font, color, or material) — blank fields inherit');

    // Case 1: user clicked a tile — save to that single occurrence
    if (selectedOcc) return saveOverrideToOccs([selectedOcc], patch);

    // Case 2: typed target
    if (!target.trim()) return toast.error('Type a target or click a letter tile');
    const res = resolveTypedTarget(target);
    if (!res || !res.matches.length) return toast.error('No matches in your text for that target');
    if (res.matches.length === 1) return saveOverrideToOccs(res.matches, patch);
    // Ambiguous — show inline scope choice
    setScopePrompt(res);
  };

  const applyScope = (scope) => {
    if (!scopePrompt) return;
    const patch = buildPatch();
    let list = scopePrompt.matches;
    if (scope === 'first') list = list.slice(0, 1);
    if (scope === 'firstword') {
      const first = list[0];
      list = list.filter(m => m.wordIndex === first?.wordIndex && m.line === first?.line);
    }
    saveOverrideToOccs(list, patch);
  };

  const applyToAllMatchingChar = () => {
    // Source: selected occurrence's saved override, or last override in the list
    let src = selectedOcc ? overrideForOcc(selectedOcc.id) : null;
    if (!src) src = overrides[overrides.length - 1];
    if (!src) return toast.error('Save an override first, then use Apply To All Matching');
    const targetChar = (src.char || '').toLowerCase();
    if (!targetChar) return toast.error('Cannot infer a character to match from that override');
    const matches = occurrences.filter(o => o.char.toLowerCase() === targetChar);
    const next = overrides.filter(o => !matches.some(m => m.id === o.occurrenceId));
    matches.forEach(m => next.push({ id: `ov-${Date.now()}-${m.id}`, occurrenceId: m.id, char: m.char, label: m.label, overrides: { ...src.overrides } }));
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: next });
    toast.success(`Applied to all ${matches.length} "${src.char}" occurrences (explicit action)`);
  };

  const clearSelected = () => {
    if (!selectedOccId) return;
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: overrides.filter(o => o.occurrenceId !== selectedOccId) });
    toast.success('Override cleared');
  };

  const copyStyle = () => {
    const src = selectedOcc ? overrideForOcc(selectedOcc.id) : overrides[overrides.length - 1];
    if (!src) return toast.error('Nothing to copy');
    window.__teCopiedStyle = src.overrides;
    toast.success('Style copied');
  };
  const pasteStyle = () => {
    if (!window.__teCopiedStyle) return toast.error('Nothing to paste');
    if (!selectedOcc) return toast.error('Pick a tile first');
    const next = overrides.filter(o => o.occurrenceId !== selectedOcc.id);
    next.push({ id: `ov-${Date.now()}-${selectedOcc.id}`, occurrenceId: selectedOcc.id, char: selectedOcc.char, label: selectedOcc.label, overrides: { ...window.__teCopiedStyle } });
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: next });
    toast.success('Style pasted');
  };
  const lockSelected = () => {
    if (!selectedOcc) return;
    const existing = overrideForOcc(selectedOcc.id);
    const merged = { ...(existing?.overrides || {}), locked: true };
    const next = overrides.filter(o => o.occurrenceId !== selectedOcc.id);
    next.push({ id: existing?.id || `ov-${Date.now()}-${selectedOcc.id}`, occurrenceId: selectedOcc.id, char: selectedOcc.char, label: selectedOcc.label, overrides: merged });
    dispatch({ type: 'SET', key: 'perLetterOverrides', value: next });
  };

  return (
    <SectionCtx.Provider value={onlyGroup}>
      <S title="Advanced Letter Selection">
        <p className="text-sm text-zinc-400 mb-2">Batch-apply an override across a smart selection. Each occurrence still gets its own independent override — nothing gets merged.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="sel-vowels" onClick={() => applyPresetToOccs('vowels only', o => VOWELS.includes(o.char))} className="chip">All Vowels</button>
          <button data-testid="sel-cons" onClick={() => applyPresetToOccs('consonants only', o => /[A-Za-z]/.test(o.char) && !VOWELS.includes(o.char))} className="chip">All Consonants</button>
          <button data-testid="sel-alt" onClick={() => applyPresetToOccs('alternating letters', o => o.globalIndex % 2 === 0)} className="chip">Alternating Letters</button>
          <button data-testid="sel-firstword" onClick={() => applyPresetToOccs('first word', o => o.line === 0 && o.wordIndex === 0)} className="chip">First Word</button>
          <button data-testid="sel-firstline" onClick={() => applyPresetToOccs('first line', o => o.line === 0)} className="chip">First Line</button>
          <button data-testid="sel-all" onClick={() => applyPresetToOccs('all letters', o => o.char !== ' ')} className="chip">Select All (Individual)</button>
        </div>
      </S>

      <S title="Per-Letter Overrides (each occurrence is independent)">
        <p className="text-sm text-zinc-400">Every occurrence of a repeated letter has its own tile. Clicking or editing one only affects that one. Blank fields inherit from your global recipe.</p>
        {!occurrences.length && <p className="text-xs text-zinc-500 mt-2">Type your text in Quick Create first to see occurrence tiles.</p>}
        <div className="flex flex-wrap gap-2 mt-3" data-testid="occurrence-grid">
          {occurrences.map((occ) => {
            const has = !!overrideForOcc(occ.id);
            const isSel = selectedOccId === occ.id;
            return (
              <button
                key={occ.id}
                data-testid={`letter-tile-${occ.id}`}
                onClick={() => selectTile(occ)}
                title={occ.label}
                className={`relative w-11 h-11 rounded-lg font-display text-2xl chrome-border transition-colors ${isSel ? 'bg-pink-500/30 border-pink-500 text-white' : has ? 'bg-emerald-500/15 border-emerald-500 text-white' : 'bg-zinc-950 text-zinc-300'}`}
              >
                {occ.char === ' ' ? '·' : occ.char}
                <span className="absolute -top-1 -right-1 text-[9px] leading-none text-zinc-500 font-mono">{occ.occurrenceOfChar || ''}</span>
              </button>
            );
          })}
        </div>
        {selectedOcc && (
          <div className="mt-2 text-xs text-zinc-400" data-testid="selected-occ-hint"><span className="text-pink-400">Selected:</span> {selectedOcc.label} · click the same tile again to deselect and remove its override</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <input data-testid="letter-target" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" placeholder="Or type a target (letter, word, line)" value={target} onChange={(e) => { setTarget(e.target.value); setSelectedOccId(''); setScopePrompt(null); }} />
          <input data-testid="letter-font" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" placeholder="Font direction override" value={font} onChange={(e) => setFont(e.target.value)} />
          <input data-testid="letter-material" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" placeholder="Material override" value={material} onChange={(e) => setMaterial(e.target.value)} />
          <div className="flex items-center gap-2">
            <input type="color" data-testid="letter-color" value={color} onChange={(e) => { setColor(e.target.value); setColorTouched(true); }} className="w-12 h-10 bg-transparent" />
            <span className="text-sm text-zinc-400">{colorTouched ? `Color = ${color}` : 'Color override (untouched = inherit)'}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid="letter-copy" onClick={copyStyle} className="chip">Copy Style</button>
          <button data-testid="letter-paste" onClick={pasteStyle} className="chip">Paste Style</button>
          <button data-testid="letter-lock" onClick={lockSelected} className="chip">Lock Letter</button>
          <button data-testid="letter-apply-matching" onClick={applyToAllMatchingChar} className="chip" title="Explicit: apply saved override to every matching character">Apply To All Matching (Explicit)</button>
          <button data-testid="letter-clear" onClick={() => dispatch({ type: 'SET', key: 'perLetterOverrides', value: [] })} className="chip">Clear All</button>
        </div>

        <button data-testid="letter-add" onClick={addOverride} className="btn-magenta mt-4 rounded-full px-4 py-2 text-xs uppercase tracking-widest">Add Override</button>

        {scopePrompt && (
          <div className="mt-3 p-3 rounded-lg border border-pink-500/40 bg-pink-500/5" data-testid="scope-prompt">
            <div className="text-xs text-zinc-200 mb-2">"{target}" matches <span className="text-pink-400">{scopePrompt.matches.length} occurrences</span> — {scopePrompt.description}. Choose scope:</div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="scope-first" onClick={() => applyScope('first')} className="chip">Apply to first match only</button>
              <button data-testid="scope-firstword" onClick={() => applyScope('firstword')} className="chip">Apply to first word only</button>
              <button data-testid="scope-all" onClick={() => applyScope('all')} className="chip">Apply to all {scopePrompt.matches.length} (explicit)</button>
              <button data-testid="scope-cancel" onClick={() => setScopePrompt(null)} className="chip">Cancel</button>
            </div>
          </div>
        )}

        {overrides.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="overrides-list">
            {overrides.map((o, i) => {
              const label = o.label || (typeof o.target === 'string' ? `"${o.target}" (legacy)` : `override ${i + 1}`);
              return (
                <div key={o.id || i} className="flex items-center justify-between chrome-border rounded-lg px-3 py-2 text-xs" data-testid={`override-row-${o.occurrenceId || i}`}>
                  <span className="text-zinc-300">{label} · {Object.entries(o.overrides || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ') || 'no fields set'}</span>
                  <button data-testid={`override-remove-${o.occurrenceId || i}`} onClick={() => dispatch({ type: 'SET', key: 'perLetterOverrides', value: overrides.filter((_, x) => x !== i) })} className="text-pink-500 uppercase tracking-widest">Remove</button>
                </div>
              );
            })}
          </div>
        )}
      </S>

      <S title="Color Lab · HSL Wheel + Harmony">
        <ColorLab
          swatches={state.customPalette || []}
          onSwatches={(list) => dispatch({ type: 'SET', key: 'customPalette', value: list })}
          gradient={state.gradient || { type: 'Linear', angle: 90, stops: [] }}
          onGradient={(g) => dispatch({ type: 'SET', key: 'gradient', value: g })}
          target={state.colorTarget || 'Main Text Fill'}
          onTarget={(t) => dispatch({ type: 'SET', key: 'colorTarget', value: t })}
          fillMode={state.fillMode || 'Solid Color'}
          onFillMode={(m) => dispatch({ type: 'SET', key: 'fillMode', value: m })}
          solidColor={state.solidColor || '#FF107A'}
          onSolidColor={(c) => dispatch({ type: 'SET', key: 'solidColor', value: c })}
          solidOpacity={state.solidOpacity ?? 100}
          onSolidOpacity={(o) => dispatch({ type: 'SET', key: 'solidOpacity', value: o })}
          targetFills={state.targetFills || {}}
          onTargetFills={(tf) => dispatch({ type: 'SET', key: 'targetFills', value: tf })}
        />
      </S>
      <CharacterSheetSection state={state} dispatch={dispatch} />
    </SectionCtx.Provider>
  );
}

function HolidayTab({ state, set, setCustom, chooseTopicCategory }) {
  const applyTreatment = (treatment) => {
    // Build a patch — additive, do not blindly overwrite user selections that are already made
    const patch = { holidayTreatment: treatment.id };
    if (treatment.fontStyle && !state.fontStyle) patch.fontStyle = treatment.fontStyle;
    if (treatment.materialFamily) patch.materialFamily = treatment.materialFamily;
    if (treatment.materialSubtype) patch.materialSubtype = treatment.materialSubtype;
    if (treatment.finish) patch.finish = treatment.finish;
    if (treatment.paletteRef && PALETTES[treatment.paletteRef]) patch.palette = treatment.paletteRef;
    if (treatment.supporting) {
      const cur = new Set(state.supporting || []);
      treatment.supporting.forEach(s => cur.add(s));
      patch.supporting = Array.from(cur);
    }
    // custom entry appends description
    patch.customEntries = { ...(state.customEntries || {}), holidayTreatment: `${treatment.label}: ${treatment.description}` };
    // Use dispatch via set() shim — set only updates one key. Emulate PATCH via multiple sets.
    Object.entries(patch).forEach(([k, v]) => set(k, v));
  };
  return (
    <>
      <S title="Pick A Holiday, Season Or Industry">
        <SimpleSelect testId="holiday-topic-select" value={state.topicCategory} onChange={chooseTopicCategory} options={TOPIC_CATEGORIES} />
        <FieldLabel>Specific Subtopic</FieldLabel>
        <SimpleSelect testId="holiday-subtopic-select" value={state.topicItem} onChange={(v) => set('topicItem', v)} options={TOPIC_ITEMS[state.topicCategory] || []} />
        <CustomEntryField testId="holiday-custom" value={state.topicCustom} onChange={(v) => set('topicCustom', v)} />
        <p className="text-xs text-zinc-500 mt-2">Tip: visit the Presets page to load a complete editable holiday recipe (e.g. Chrome Vampire, Pink Slime, Christmas Frost).</p>
      </S>
      <S title="Holiday & Seasonal Treatments">
        <p className="text-sm text-zinc-400 mb-2">Tap a treatment to add its real font direction, material, palette, finish and props to your recipe. Additive — nothing you already picked will be erased.</p>
        <div className="space-y-4">
          {Object.entries(HOLIDAY_TREATMENTS).map(([group, list]) => (
            <div key={group}>
              <FieldLabel>{group}</FieldLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {list.map(t => (
                  <button
                    key={t.id}
                    data-testid={`holiday-treatment-${t.id}`}
                    onClick={() => applyTreatment(t)}
                    className={`text-left rounded-xl px-3 py-3 text-xs transition-colors ${state.holidayTreatment === t.id ? 'bg-pink-500/20 border border-pink-500 text-white' : 'chrome-border chrome-hover bg-zinc-950 text-zinc-300'}`}
                  >
                    <div className="text-sm text-white mb-1">{t.label}</div>
                    <div className="text-[11px] text-zinc-400 leading-snug">{t.description}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </S>
      <S title="Palette Suggestions">
        <MultiChipSelect testIdPrefix="palette-chip" options={Object.keys(PALETTES)} value={[state.palette]} onToggle={(v) => set('palette', v)} />
      </S>
    </>
  );
}

function SupportingTab({ state, toggle, setCustom }) {
  return (
    <S title="Supporting Elements">
      <p className="text-sm text-zinc-400 mb-3">Add decorations, atmosphere, and story props. They will decorate around the exact typography without hiding it.</p>
      {Object.entries(SUPPORTING_ELEMENTS).map(([group, items]) => (
        <div key={group} className="space-y-2">
          <FieldLabel>{group}</FieldLabel>
          <MultiChipSelect testIdPrefix={`sup-${group}`} options={items} value={state.supporting} onToggle={(v) => toggle('supporting', v)} />
        </div>
      ))}
      <CustomEntryField testId="supporting-custom" value={state.customEntries.supporting} onChange={(v) => setCustom('supporting', v)} />
    </S>
  );
}

function CompositionTab({ state, set, setCustom }) {
  const intensity = state.intensity || {};
  const setI = (k, v) => set('intensity', { ...intensity, [k]: v });
  const sliders = ['Creative Level','3D Depth','Gloss','Texture','Distress','Distortion','Curve','Arch','Puffiness','Outline Thickness','Shadow','Lighting','Element Quantity','Realism','Exaggeration','Complexity'];
  return (
    <>
      <S title="Composition Layout"><SimpleSelect testId="composition-select" value={state.composition} onChange={(v) => set('composition', v)} options={COMPOSITION_LAYOUTS} /><CustomEntryField testId="composition-custom" value={state.customEntries.composition} onChange={(v) => setCustom('composition', v)} /></S>
      <S title="Text Hierarchy"><SimpleSelect testId="hierarchy-select" value={state.hierarchy} onChange={(v) => set('hierarchy', v)} options={HIERARCHY_PRESETS} /></S>
      <S title="Focal Point & Realism">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SimpleSelect testId="focal-select" value={state.focalPoint} onChange={(v) => set('focalPoint', v)} options={['Center Hero Text','Subject Left / Text Right','Product Pedestal','Character Ensemble','Text Overlaps Subject']} />
          <SimpleSelect testId="realism-select" value={state.realism} onChange={(v) => set('realism', v)} options={['Photorealistic','Semi-Realistic','Illustrated','Painterly','Vector Flat','3D Render','Retro Poster','Mixed Media']} />
        </div>
      </S>
      <S title="Prompt Strength · Intensity">
        <p className="text-xs text-zinc-400 mb-2">Dial any lever from 0-100 to weight the AI toward that quality. Zero = untouched, 100 = maxed out.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sliders.map((label) => (
            <label key={label} className="text-xs text-zinc-400 space-y-1">
              <div className="flex justify-between"><span>{label}</span><span className="text-zinc-200">{intensity[label] ?? 0}%</span></div>
              <input type="range" min="0" max="100" value={intensity[label] ?? 0} onChange={(e) => setI(label, +e.target.value)} className="w-full accent-pink-500" data-testid={`in-${label.toLowerCase().replace(/\s/g, '-')}`} />
            </label>
          ))}
        </div>
      </S>
      <S title="Lighting & Camera">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SimpleSelect testId="lighting-select" value={state.lighting} onChange={(v) => set('lighting', v)} options={LIGHTING} />
          <SimpleSelect testId="camera-select" value={state.camera} onChange={(v) => set('camera', v)} options={CAMERA_ANGLES} />
        </div>
        <SimpleSelect testId="aspect-select" value={state.aspect} onChange={(v) => set('aspect', v)} options={['Square 1:1','Portrait 4:5','Portrait 2:3','Landscape 3:2','Landscape 16:9','Story 9:16','DTF 4500×5400','Tumbler 20oz Wrap','Tumbler 15oz Wrap','Letter 8.5×11','Legal 8.5×14','Custom']} />
      </S>
    </>
  );
}


/* ---------- Spacing & Custom Lettering & Character Sheet ---------- */

function SpacingSection({ state, set }) {
  const { tracking, wordSpacing, lineSpacing, stackGap } = SPACING_PRESETS;
  const Row = ({ label, testId, presets, value, min, max, onChange, help }) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <FieldLabel>{label}</FieldLabel>
        <span className="text-xs text-zinc-400 font-mono">{value}</span>
      </div>
      <div className="flex flex-wrap gap-1 mb-1">
        {presets.map(p => (
          <button key={p.label} data-testid={`${testId}-${p.label.toLowerCase().replace(/\s/g,'-')}`}
            onClick={() => onChange(p.val)}
            className={`chip ${value === p.val ? 'bg-pink-500/20 border-pink-500 text-white' : ''}`}>{p.label}</button>
        ))}
        <button data-testid={`${testId}-reset`} onClick={() => onChange(0)} className="chip">Reset</button>
      </div>
      <input data-testid={testId} type="range" min={min} max={max} value={value} onChange={(e) => onChange(+e.target.value)} className="w-full accent-pink-500" />
      {help && <p className="text-[10px] text-zinc-500">{help}</p>}
    </div>
  );
  return (
    <S title="Letter, Word, Line & Stack Spacing">
      <p className="text-xs text-zinc-400 mb-2">Adjust how tight or airy the lettering feels. Values are translated into visual language the AI understands — not exact pixel/em values.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Row label="Letter Tracking" testId="sp-tracking" presets={tracking} value={state.tracking ?? 0} min={-15} max={30} onChange={(v) => set('tracking', v)} help="Negative = tighter letters, positive = wider spacing" />
        <Row label="Word Spacing" testId="sp-word" presets={wordSpacing} value={state.wordSpacing ?? 0} min={-5} max={20} onChange={(v) => set('wordSpacing', v)} />
        <Row label="Line Spacing / Leading" testId="sp-line" presets={lineSpacing} value={state.lineSpacing ?? 0} min={-15} max={30} onChange={(v) => set('lineSpacing', v)} />
        <Row label="Stack Gap (Between Stacked Lines)" testId="sp-stack" presets={stackGap} value={state.stackGap ?? 0} min={-20} max={30} onChange={(v) => set('stackGap', v)} help="Negative = overlapping stacked lines" />
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs text-zinc-300">
        <input data-testid="sp-overlap" type="checkbox" checked={!!state.controlledOverlap} onChange={(e) => set('controlledOverlap', e.target.checked)} className="accent-pink-500" />
        Controlled letter overlap (intentional, still readable)
      </label>
      <div className="mt-3 chrome-border rounded-lg bg-zinc-950 p-3">
        <div className="eyebrow mb-1">Layout preview · browser fallback font</div>
        <div className="text-white break-words" style={{
          letterSpacing: `${(state.tracking ?? 0) / 50}em`,
          wordSpacing: `${(state.wordSpacing ?? 0) / 20}em`,
          lineHeight: 1 + ((state.lineSpacing ?? 0) / 40),
          fontFamily: 'inherit',
        }} data-testid="sp-preview">
          {(state.exactText || 'PREVIEW TEXT').split('\n').map((line, i) => (<div key={i}>{line}</div>))}
        </div>
        <p className="text-[10px] text-zinc-500 mt-1">Layout preview only — does not represent the final generated material, effect or font.</p>
      </div>
    </S>
  );
}

function CustomLetteringSection({ state, set, setCustom }) {
  const lettering = state.lettering || {};
  const setL = (k, v) => set('lettering', { ...lettering, [k]: v });
  return (
    <S title="Custom Lettering Controls">
      <p className="text-xs text-zinc-400 mb-3">Design your own invented lettering. Every control below feeds natural-language directives into the compiled prompt.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(LETTERING_CONTROLS).map(([key, opts]) => (
          <div key={key} className="space-y-1">
            <FieldLabel>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</FieldLabel>
            <SimpleSelect testId={`lc-${key}`} value={lettering[key] || ''} onChange={(v) => setL(key, v === '(unset)' ? '' : v)} options={opts} />
          </div>
        ))}
      </div>
      <CustomEntryField testId="lettering-custom" label="Describe your own invented font direction in one sentence" placeholder="e.g. tall condensed sans with knife-cut terminals and rounded counters, small drop-shaped serifs" value={state.customEntries.lettering} onChange={(v) => setCustom('lettering', v)} />
    </S>
  );
}

function CharacterSheetSection({ state, dispatch }) {
  const cs = state.characterSheet || { enabled: false, chars: '' };
  const setCS = (patch) => dispatch({ type: 'SET', key: 'characterSheet', value: { ...cs, ...patch } });
  return (
    <S title="Alphabet / Character Sheet Prompt Mode">
      <p className="text-xs text-zinc-400 mb-2">Generates a distinct prompt asking the AI to render your selected characters as a coordinated set (letters, numbers, symbols). This creates a lettering artwork sheet, not an installable OTF/TTF font.</p>
      <label className="flex items-center gap-2 text-sm text-zinc-200 mb-2">
        <input data-testid="cs-enable" type="checkbox" checked={!!cs.enabled} onChange={(e) => setCS({ enabled: e.target.checked })} className="accent-pink-500" />
        Enable character-sheet output
      </label>
      {cs.enabled && (
        <>
          <div className="flex flex-wrap gap-2 mb-2">
            {CHARACTER_SET_PRESETS.map(p => (
              <button key={p.id} data-testid={`cs-preset-${p.id}`} onClick={() => setCS({ chars: p.chars })} className="chip">{p.label}</button>
            ))}
          </div>
          <textarea data-testid="cs-chars" rows={2} value={cs.chars} onChange={(e) => setCS({ chars: e.target.value })} placeholder="Type any characters you want on the sheet (e.g. ABC123!?)" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <p className="text-[10px] text-zinc-500 mt-1">Long character sets are split into coordinated multi-image prompts automatically.</p>
        </>
      )}
    </S>
  );
}

/* ---------- Custom Font Details Block ---------- */

const CUSTOM_FONT_FIELDS = {
  category: ['(pick)','Serif','Sans Serif','Slab Serif','Script','Calligraphy','Handwritten','Display','Decorative','Monospaced','Gothic / Blackletter','Art Deco','Art Nouveau','Varsity / Collegiate','Western','Graffiti','Retro','Y2K','Futuristic','Horror','Children','Condensed','Expanded','Chunky','Bubble','Custom'],
  weight: ['(pick)','Thin','Extra Light','Light','Regular','Medium','Semi Bold','Bold','Extra Bold','Black','Ultra Black','Custom'],
  width: ['(pick)','Compressed','Condensed','Normal','Extended','Extra Wide','Custom'],
  shape: ['(pick)','Rounded','Sharp','Angular','Wavy','Crooked','Puffy','Inflated','Hand-Drawn','Geometric','Organic','Distressed','Melted','Dripping','Stitched','Furry','Mechanical','Custom'],
  baseline: ['(pick)','Straight','Bouncing','Staggered','Wavy','Shaken','Arched','Inward Curve','Heart Formation','Stacked','Custom'],
  readability: ['(pick)','Highly Readable','Stylized but Readable','Experimental','Custom'],
  application: ['(pick)','Full Design','Line','Word','Selected Letters','Individual Character Occurrence'],
};

function CustomFontDetailsBlock({ state, dispatch, setCustom }) {
  const cf = state.customFont || {};
  const setCF = (k, v) => dispatch({ type: 'SET', key: 'customFont', value: { ...cf, [k]: v === '(pick)' ? '' : v } });
  return (
    <div className="space-y-3" data-testid="custom-font-details">
      <p className="text-xs text-zinc-400">Type absolutely any font, franchise, show, movie, cartoon, game, era, brand-style reference or lettering name. Your exact words always flow into the final prompt.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input data-testid="custom-font-name" value={cf.name || ''} onChange={(e) => setCF('name', e.target.value)} placeholder="Custom Font or Reference Name" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
        <input data-testid="custom-font-pairing" value={cf.pairing || ''} onChange={(e) => setCF('pairing', e.target.value)} placeholder="Optional pairing direction (e.g. paired with clean condensed sans)" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
      </div>
      <textarea data-testid="custom-font-desc" rows={2} value={cf.description || ''} onChange={(e) => setCF('description', e.target.value)} placeholder="Describe how it should look — e.g. chunky rounded monster letters with mismatched sizes, wide openings, a playful bouncing baseline and soft 3D edges." className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {Object.entries(CUSTOM_FONT_FIELDS).map(([key, opts]) => (
          <div key={key} className="space-y-1">
            <FieldLabel>{key[0].toUpperCase() + key.slice(1)}</FieldLabel>
            <SimpleSelect testId={`custom-font-${key}`} value={cf[key] || ''} onChange={(v) => setCF(key, v)} options={opts} />
          </div>
        ))}
      </div>
      <CustomEntryField testId="custom-font-extra" placeholder="Any extra description or reference (optional)" value={state.customEntries.customFont} onChange={(v) => setCustom('customFont', v)} />
    </div>
  );
}

/* ---------- Font Combo Builder ---------- */

const COMBO_ROLES = [
  { key: 'hero', label: 'Hero Font', hint: 'Main statement — biggest text' },
  { key: 'secondary', label: 'Secondary Font', hint: 'Supporting headline / subhead' },
  { key: 'scriptAccent', label: 'Script Accent', hint: 'Signature or accent word' },
  { key: 'smallSupporting', label: 'Small Supporting Text', hint: 'Body / caption / detail' },
];

const COMBO_SCOPES = ['Full Design','Line','Word','Selected Letters','Individual Character Occurrence'];

function FontComboBuilder({ state, dispatch }) {
  const combo = state.fontCombo || {};
  const setRole = (roleKey, patch) => dispatch({ type: 'SET', key: 'fontCombo', value: { ...combo, [roleKey]: { ...(combo[roleKey] || {}), ...patch } } });
  const clearAll = () => dispatch({ type: 'SET', key: 'fontCombo', value: null });
  const roleFilled = (r) => r && (r.namedFont || r.creativeFont || r.customName || r.customDescription);
  const anySet = COMBO_ROLES.some(r => roleFilled(combo[r.key]));
  return (
    <div className="space-y-3" data-testid="font-combo">
      <p className="text-xs text-zinc-400">Combine multiple font directions in one design. Each role compiles independently. Suggestions never overwrite your selections.</p>
      {COMBO_ROLES.map(role => {
        const r = combo[role.key] || {};
        return (
          <div key={role.key} className="chrome-border bg-zinc-950/60 rounded-lg p-3 space-y-2" data-testid={`combo-${role.key}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white">{role.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{role.hint}</div>
              </div>
              <select data-testid={`combo-${role.key}-scope`} value={r.scope || ''} onChange={(e) => setRole(role.key, { scope: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs">
                <option value="">Scope: choose…</option>
                {COMBO_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input data-testid={`combo-${role.key}-named`} value={r.namedFont || ''} onChange={(e) => setRole(role.key, { namedFont: e.target.value })} placeholder="Existing font name (from Font Library)" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
              <select data-testid={`combo-${role.key}-creative`} value={r.creativeFontId || ''} onChange={(e) => {
                const cf = CREATIVE_FONTS.find(x => x.id === e.target.value);
                setRole(role.key, { creativeFontId: e.target.value, creativeFont: cf || null });
              }} className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm">
                <option value="">Creative reference (optional)</option>
                {CREATIVE_FONT_CATEGORIES.map(c => (
                  <optgroup key={c} label={c}>
                    {CREATIVE_FONTS.filter(f => f.cat === c).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <input data-testid={`combo-${role.key}-custom-name`} value={r.customName || ''} onChange={(e) => setRole(role.key, { customName: e.target.value })} placeholder="Or type any custom name" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
              <input data-testid={`combo-${role.key}-custom-desc`} value={r.customDescription || ''} onChange={(e) => setRole(role.key, { customDescription: e.target.value })} placeholder="Describe its visual style (optional)" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            </div>
            {roleFilled(r) && (
              <button data-testid={`combo-${role.key}-clear`} onClick={() => setRole(role.key, { namedFont:'', creativeFontId:'', creativeFont:null, customName:'', customDescription:'', scope:'' })} className="chip">Clear this role</button>
            )}
          </div>
        );
      })}
      {anySet && (
        <button data-testid="combo-clear-all" onClick={clearAll} className="chip">Clear All Combo Roles</button>
      )}
    </div>
  );
}

